/**
 *  Shoppable Reels
 *  - <reels-carousel>: homepage row of reel thumbnails (arrows, edge fades, opens the player)
 *  - <reels-player>: full-screen vertical feed (snippets/reels-player.liquid)
 */
if (!customElements.get('reels-carousel')) {
  class ReelsCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.reels-carousel__track');
      this.prev = this.querySelector('.flickity-prev');
      this.next = this.querySelector('.flickity-next');
      if (!this.track) return;

      this._frame = null;
      this._onScroll = () => {
        if (this._frame) return;
        this._frame = requestAnimationFrame(() => {
          this._frame = null;
          this.update();
        });
      };
      this._onClick = (event) => {
        const thumb = event.target.closest('[data-reel-index]');
        if (!thumb || !this.contains(thumb)) return;
        document.dispatchEvent(new CustomEvent('reels:open', {
          detail: {
            index: parseInt(thumb.dataset.reelIndex, 10) || 0,
            handle: thumb.dataset.reelHandle,
            sectionId: this.dataset.sectionId,
            trigger: thumb
          }
        }));
      };

      this.track.addEventListener('scroll', this._onScroll, { passive: true });
      this.addEventListener('click', this._onClick);
      this.prev?.addEventListener('click', () => this.go(-1));
      this.next?.addEventListener('click', () => this.go(1));

      this.resizeObserver = new ResizeObserver(this._onScroll);
      this.resizeObserver.observe(this.track);
      window.addEventListener('load', this._onScroll, { once: true });
      this.update();
      this.setupPreview();
    }

    disconnectedCallback() {
      window.removeEventListener('load', this._onScroll);
      this.track?.removeEventListener('scroll', this._onScroll);
      this.removeEventListener('click', this._onClick);
      this.resizeObserver?.disconnect();
      if (this._pick) {
        this.track.removeEventListener('scroll', this._onPreviewScroll);
        document.removeEventListener('visibilitychange', this._pick);
        document.removeEventListener('reels:open', this._onPlayerOpen);
        document.removeEventListener('reels:close', this._pick);
        this.viewObserver?.disconnect();
        this.showPreview(null);
      }
    }

    /* Short silent preview loop on the first fully visible reel */
    setupPreview() {
      const seconds = parseFloat(this.dataset.preview);
      const connection = navigator.connection || {};
      if (!seconds || connection.saveData || /2g/.test(connection.effectiveType || '') ||
        window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      this.previewLength = seconds;
      this.inView = false;
      this._pick = () => this.pickPreview();
      this._onPreviewScroll = () => {
        clearTimeout(this._previewTimer);
        this._previewTimer = setTimeout(this._pick, 150);
      };
      this._onPlayerOpen = () => this.showPreview(null);

      const start = () => {
        this.viewObserver = new IntersectionObserver(([entry]) => {
          this.inView = entry.isIntersecting;
          this.pickPreview();
        }, { threshold: 0.4 });
        this.viewObserver.observe(this.track);
      };
      // Wait for the page to finish loading so previews never slow the first view
      if (document.readyState === 'complete') start();
      else window.addEventListener('load', start, { once: true });

      this.track.addEventListener('scroll', this._onPreviewScroll, { passive: true });
      document.addEventListener('visibilitychange', this._pick);
      document.addEventListener('reels:open', this._onPlayerOpen);
      document.addEventListener('reels:close', this._pick);
    }

    pickPreview() {
      if (!this.inView || document.hidden || document.documentElement.classList.contains('reels-open')) {
        this.showPreview(null);
        return;
      }
      const box = this.track.getBoundingClientRect();
      const media = Array.from(this.querySelectorAll('[data-preview-src]')).find((el) => {
        const rect = el.getBoundingClientRect();
        return rect.left >= box.left - 2 && rect.right <= box.right + 2;
      });
      this.showPreview(media || null);
    }

    showPreview(media) {
      const old = this.previewMedia;
      if (old && old !== media) {
        old.classList.remove('is-previewing');
        const oldVideo = old.querySelector('.reels-carousel__preview');
        if (oldVideo) oldVideo.pause();
      }
      this.previewMedia = media;
      if (!media) return;

      let video = media.querySelector('.reels-carousel__preview');
      if (!video) {
        video = document.createElement('video');
        video.className = 'reels-carousel__preview';
        video.muted = true;
        video.defaultMuted = true;
        video.loop = true;
        video.playsInline = true;
        video.setAttribute('muted', '');
        video.setAttribute('playsinline', '');
        video.setAttribute('aria-hidden', 'true');
        video.preload = 'auto';
        const hls = media.dataset.previewHls && video.canPlayType('application/vnd.apple.mpegurl');
        video.src = hls ? media.dataset.previewHls : media.dataset.previewSrc;
        video.addEventListener('timeupdate', () => {
          if (video.currentTime >= this.previewLength) video.currentTime = 0;
        });
        video.addEventListener('playing', () => {
          if (this.previewMedia === media) media.classList.add('is-previewing');
        });
        media.insertBefore(video, media.querySelector('.reels-carousel__play'));
      } else if (old !== media) {
        try { video.currentTime = 0; } catch (e) {}
      }
      if (video.paused) video.play().catch(() => {});
    }

    go(direction) {
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.track.scrollBy({
        left: direction * this.track.clientWidth * 0.8,
        behavior: reduce ? 'auto' : 'smooth'
      });
    }

    update() {
      const track = this.track;
      const max = track.scrollWidth - track.clientWidth;
      const pos = Math.abs(track.scrollLeft);
      const atStart = pos <= 2;
      const atEnd = pos >= max - 2;

      this.toggleAttribute('data-at-start', atStart);
      this.toggleAttribute('data-at-end', atEnd);
      if (this.prev) this.prev.disabled = atStart;
      if (this.next) this.next.disabled = atEnd;
    }
  }
  customElements.define('reels-carousel', ReelsCarousel);
}

if (!customElements.get('reels-player')) {
  const LIKED_KEY = 'hlo-reels-liked';
  const SOUND_KEY = 'hlo-reels-sound';
  const read = (store, key, fallback) => {
    try {
      const value = window[store].getItem(key);
      return value === null ? fallback : JSON.parse(value);
    } catch (e) {
      return fallback;
    }
  };
  const write = (store, key, value) => {
    try { window[store].setItem(key, JSON.stringify(value)); } catch (e) {}
  };
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const compact = (n) => {
    try { return new Intl.NumberFormat('en-GB', { notation: 'compact' }).format(n); } catch (e) { return String(n); }
  };
  const track = (name, data) => {
    try { if (window.dataLayer) window.dataLayer.push(Object.assign({ event: name }, data)); } catch (e) {}
    try { if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) Shopify.analytics.publish(name, data); } catch (e) {}
  };

  class ReelsPlayer extends HTMLElement {
    connectedCallback() {
      this.feed = this.querySelector('.reels-player__feed');
      this.reels = Array.from(this.querySelectorAll('.reels-player__reel'));
      this.backdrop = this.querySelector('.reels-player__backdrop');
      this.toastEl = this.querySelector('.reels-player__toast');
      this.muteButton = this.querySelector('[data-reels-mute]');
      this.active = -1;
      this.liked = read('localStorage', LIKED_KEY, []);
      this.reels.forEach((reel) => {
        this.renderLike(reel, false);
        const video = reel.querySelector('video');
        video.addEventListener('timeupdate', () => {
          reel.style.setProperty('--progress', video.duration ? video.currentTime / video.duration : 0);
        });
      });

      this._onOpen = (event) => {
        if (event.detail && event.detail.sectionId === this.dataset.sectionId) {
          this.open(event.detail.index, event.detail.trigger);
        }
      };
      this._wheelAt = 0;
      this._onKeydown = this.onKeydown.bind(this);
      document.addEventListener('reels:open', this._onOpen);
      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('wheel', this.onWheel.bind(this), { passive: false });

      // Deep link: ?reel=<handle> opens the player at that reel (first matching player only)
      const handle = new URLSearchParams(window.location.search).get('reel');
      const index = this.reels.findIndex((reel) => reel.dataset.handle === handle);
      if (handle && index > -1 && !window.__reelsDeepLinked) {
        window.__reelsDeepLinked = true;
        this.open(index);
      }
    }

    disconnectedCallback() {
      document.removeEventListener('reels:open', this._onOpen);
      if (!this.hidden) this.close();
    }

    get current() {
      return this.reels[this.active];
    }

    eventData(reel, extra) {
      return Object.assign({ reel_handle: reel.dataset.handle, reel_index: Number(reel.dataset.index) }, extra);
    }

    open(index, trigger) {
      const reel = this.reels[index];
      if (!reel) return;
      this.trigger = trigger || document.activeElement;
      this.hidden = false;
      document.documentElement.classList.add('reels-open');
      document.addEventListener('keydown', this._onKeydown);
      // Sound on by default (the tap that opened the player lets browsers allow it). If a browser
      // still blocks it, play() falls back to muted. A visitor's mute choice is kept for the visit.
      this.muted = !read('sessionStorage', SOUND_KEY, true);
      this.syncMute();
      this.reels.forEach((r) => {
        const video = r.querySelector('video');
        if (video.dataset.poster && !video.poster) video.poster = video.dataset.poster;
      });
      this.feed.scrollTop = reel.offsetTop;
      this.reels.forEach((r) => this.collapseCaption(r));

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) this.setActive(this.reels.indexOf(entry.target));
        });
      }, { root: this.feed, threshold: 0.6 });
      this.reels.forEach((r) => this.observer.observe(r));

      this.setActive(index);
      this.querySelector('[data-reels-close]').focus({ preventScroll: true });
      track('reel_open', this.eventData(reel));
    }

    close() {
      if (this.hidden) return;
      this.hidden = true;
      document.documentElement.classList.remove('reels-open');
      document.removeEventListener('keydown', this._onKeydown);
      if (this.observer) this.observer.disconnect();
      this.reels.forEach((reel) => this.stop(reel));
      this.active = -1;
      history.replaceState(history.state, '', this.urlFor(null));
      document.dispatchEvent(new CustomEvent('reels:close', { detail: { sectionId: this.dataset.sectionId } }));
      if (this.trigger && this.trigger.focus && document.contains(this.trigger)) {
        this.trigger.focus({ preventScroll: true });
      }
    }

    setActive(index) {
      if (index === this.active || index < 0) return;
      this.active = index;
      this.reels.forEach((reel, n) => {
        const video = reel.querySelector('video');
        reel.inert = n !== index;
        if (n !== index) this.collapseCaption(reel);
        if (n === index || n === index + 1) this.load(video, 'auto');
        else if (n === index - 1) this.load(video, 'metadata');
        if (n === index) {
          video.muted = this.muted;
          reel.classList.remove('is-paused');
          this.play(video);
        } else {
          this.stop(reel);
        }
      });

      // Focus can be left inside a reel that just became inert: keep it in the player
      if (!this.contains(document.activeElement) || document.activeElement.closest('[inert]')) {
        this.focus({ preventScroll: true });
      }

      const reel = this.current;
      const poster = reel.querySelector('video').dataset.poster;
      this.backdrop.style.backgroundImage = poster ? 'url("' + poster + '")' : '';
      this.querySelector('[data-reels-prev]').disabled = index === 0;
      this.querySelector('[data-reels-next]').disabled = index === this.reels.length - 1;
      history.replaceState(history.state, '', this.urlFor(reel.dataset.handle));
      track('reel_view', this.eventData(reel));
    }

    load(video, preload) {
      if (!video.getAttribute('src') && video.dataset.src) {
        const hls = video.dataset.hls && video.canPlayType('application/vnd.apple.mpegurl');
        video.preload = preload;
        video.src = hls ? video.dataset.hls : video.dataset.src;
      } else if (preload === 'auto') {
        video.preload = 'auto';
      }
    }

    play(video) {
      const attempt = video.play();
      if (attempt) {
        attempt.catch(() => {
          // Browsers block autoplay with sound until the visitor interacts: fall back to muted
          if (!video.muted) {
            this.muted = true;
            this.syncMute();
            video.muted = true;
            video.play().catch(() => {});
          }
        });
      }
    }

    stop(reel) {
      const video = reel.querySelector('video');
      if (!video.paused) video.pause();
      if (video.currentTime) {
        try { video.currentTime = 0; } catch (e) {}
      }
      reel.style.setProperty('--progress', 0);
      reel.classList.remove('is-paused', 'is-flashing');
    }

    go(step) {
      const reel = this.reels[Math.max(0, Math.min(this.reels.length - 1, this.active + step))];
      if (reel) this.feed.scrollTo({ top: reel.offsetTop, behavior: reduceMotion() ? 'auto' : 'smooth' });
    }

    togglePlay(reel) {
      const video = reel.querySelector('video');
      const toggle = reel.querySelector('[data-reels-toggle]');
      if (video.paused) {
        this.play(video);
        reel.classList.remove('is-paused');
        toggle.setAttribute('aria-label', 'Pause');
      } else {
        video.pause();
        reel.classList.add('is-paused');
        toggle.setAttribute('aria-label', 'Play');
      }
      reel.classList.remove('is-flashing');
      void reel.offsetWidth;
      reel.classList.add('is-flashing');
    }

    // Caption: two lines with "more" when it doesn't fit, full text when expanded
    collapseCaption(reel) {
      const caption = reel.querySelector('.reels-player__caption');
      const text = reel.querySelector('.reels-player__caption-text');
      const more = reel.querySelector('[data-reels-more]');
      if (!caption || !text || !more) return;
      caption.classList.remove('is-expanded');
      reel.classList.remove('is-caption-open');
      more.textContent = 'more';
      more.setAttribute('aria-expanded', 'false');
      more.hidden = text.scrollHeight <= text.clientHeight + 1;
    }

    toggleCaption(reel) {
      const caption = reel.querySelector('.reels-player__caption');
      const more = reel.querySelector('[data-reels-more]');
      if (caption.classList.contains('is-expanded')) {
        this.collapseCaption(reel);
        return;
      }
      caption.classList.add('is-expanded');
      reel.classList.add('is-caption-open');
      more.textContent = 'less';
      more.setAttribute('aria-expanded', 'true');
    }

    syncMute() {
      this.classList.toggle('is-muted', this.muted);
      this.muteButton.setAttribute('aria-pressed', String(!this.muted));
      this.muteButton.setAttribute('aria-label', this.muted ? 'Unmute' : 'Mute');
    }

    toggleMute() {
      this.muted = !this.muted;
      write('sessionStorage', SOUND_KEY, !this.muted);
      this.syncMute();
      const video = this.current && this.current.querySelector('video');
      if (video) {
        video.muted = this.muted;
        if (video.paused && !this.current.classList.contains('is-paused')) this.play(video);
      }
    }

    renderLike(reel, animate) {
      const liked = this.liked.includes(reel.dataset.handle);
      const button = reel.querySelector('[data-reels-like]');
      const count = (parseInt(reel.dataset.likes, 10) || 0) + (liked ? 1 : 0);
      button.classList.toggle('is-liked', liked);
      button.setAttribute('aria-pressed', String(liked));
      button.setAttribute('aria-label', (liked ? 'Unlike' : 'Like') + ', ' + count + ' likes');
      reel.querySelector('[data-reels-count]').textContent = compact(count);
      if (animate && liked && !reduceMotion()) {
        button.classList.remove('is-popping');
        void button.offsetWidth;
        button.classList.add('is-popping');
      }
    }

    toggleLike(reel) {
      const handle = reel.dataset.handle;
      const at = this.liked.indexOf(handle);
      if (at > -1) this.liked.splice(at, 1);
      else this.liked.push(handle);
      write('localStorage', LIKED_KEY, this.liked);
      this.renderLike(reel, true);
      track('reel_like', this.eventData(reel, { liked: at === -1 }));
    }

    urlFor(handle) {
      const url = new URL(window.location.href);
      if (handle) url.searchParams.set('reel', handle);
      else url.searchParams.delete('reel');
      return url.toString();
    }

    share(reel) {
      const url = this.urlFor(reel.dataset.handle);
      const done = (method) => track('reel_share', this.eventData(reel, { method: method }));
      if (navigator.share) {
        navigator.share({ title: reel.dataset.caption || document.title, url: url })
          .then(() => done('native'))
          .catch(() => {});
      } else if (navigator.clipboard) {
        navigator.clipboard.writeText(url)
          .then(() => { this.toast('Link copied'); done('copy'); })
          .catch(() => this.toast(url));
      } else {
        this.toast(url);
      }
    }

    toast(message) {
      this.toastEl.textContent = message;
      this.toastEl.classList.add('is-visible');
      clearTimeout(this._toastTimer);
      this._toastTimer = setTimeout(() => this.toastEl.classList.remove('is-visible'), 2600);
    }

    addToBag(button, reel) {
      if (button.disabled) return;
      button.disabled = true;
      button.classList.add('loading');

      const body = new FormData();
      body.append('id', button.dataset.reelsAdd);
      body.append('quantity', 1);
      body.append('sections', 'cart-drawer,cart-bubble');
      body.append('sections_url', window.location.pathname);

      fetch(window.theme && theme.routes ? theme.routes.cart_add_url : '/cart/add', {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/javascript' },
        body: body
      })
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            this.toast(response.description || response.message || 'Sorry, this could not be added to your bag');
            return;
          }
          this.renderCart(response.sections);
          track('reel_add_to_cart', this.eventData(reel, { product_handle: button.dataset.product, variant_id: Number(button.dataset.reelsAdd) }));
          if (typeof dispatchCustomEvent === 'function') {
            dispatchCustomEvent('cart:item-added', { product: response.items ? response.items[0] : response });
          }
          // Option A: close the player, then open the theme's cart drop-down
          this.close();
          const drawer = document.getElementById('Cart-Drawer');
          if (drawer && drawer.open) setTimeout(() => drawer.open(), 60);
          else document.dispatchEvent(new CustomEvent('cart:refresh'));
        })
        .catch(() => this.toast('Sorry, something went wrong. Please try again.'))
        .finally(() => {
          button.disabled = false;
          button.classList.remove('loading');
        });
    }

    renderCart(sections) {
      if (!sections) return;
      [
        ['Cart-Drawer', 'cart-drawer', '.cart-drawer'],
        ['cart-drawer-toggle', 'cart-bubble', '.thb-secondary-area--item-count'],
        ['cart-drawer-toggle', 'cart-bubble', '.thb-secondary-area--item-amount']
      ].forEach(([id, key, selector]) => {
        const host = document.getElementById(id);
        if (!host || !sections[key]) return;
        const fresh = new DOMParser().parseFromString(sections[key], 'text/html').querySelector(selector);
        if (fresh) (host.querySelector(selector) || host).innerHTML = fresh.innerHTML;
      });
    }

    onClick(event) {
      const target = event.target.closest('button, a');
      if (!target || !this.contains(target)) return;
      const reel = target.closest('.reels-player__reel');

      if (target.matches('[data-reels-close]')) this.close();
      else if (target.matches('[data-reels-mute]')) this.toggleMute();
      else if (target.matches('[data-reels-prev]')) this.go(-1);
      else if (target.matches('[data-reels-next]')) this.go(1);
      else if (!reel) return;
      else if (target.matches('[data-reels-more]')) this.toggleCaption(reel);
      else if (target.matches('[data-reels-toggle]')) this.togglePlay(reel);
      else if (target.matches('[data-reels-like]')) this.toggleLike(reel);
      else if (target.matches('[data-reels-share]')) this.share(reel);
      else if (target.matches('[data-reels-add]') && target.tagName === 'BUTTON') this.addToBag(target, reel);
      else if (target.matches('[data-reels-product]')) {
        track('reel_product_click', this.eventData(reel, { product_handle: target.dataset.reelsProduct }));
      }
    }

    onKeydown(event) {
      const key = event.key;
      if (key === 'Escape') {
        event.preventDefault();
        this.close();
      } else if (key === 'ArrowDown' || key === 'PageDown') {
        event.preventDefault();
        this.go(1);
      } else if (key === 'ArrowUp' || key === 'PageUp') {
        event.preventDefault();
        this.go(-1);
      } else if ((key === ' ' || key === 'k') && !event.target.closest('button, a') && this.current) {
        event.preventDefault();
        this.togglePlay(this.current);
      } else if (key === 'Tab') {
        this.trapFocus(event);
      }
    }

    trapFocus(event) {
      const items = Array.from(this.querySelectorAll('button, a[href]'))
        .filter((el) => !el.disabled && !el.closest('[inert]') && el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && (document.activeElement === first || !this.contains(document.activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    onWheel(event) {
      // The feed scrolls itself; wheel over the blurred background moves one reel at a time
      if (this.feed.contains(event.target)) return;
      event.preventDefault();
      const now = Date.now();
      if (now - this._wheelAt < 700 || Math.abs(event.deltaY) < 15) return;
      this._wheelAt = now;
      this.go(event.deltaY > 0 ? 1 : -1);
    }
  }
  customElements.define('reels-player', ReelsPlayer);
}
